import { KeyboardEvent as ReactKeyboardEvent, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from './cn';

type TriggerArg = { open: boolean; toggle: () => void; close: () => void };

const GAP = 6; // gap between trigger and portal panel
const MARGIN = 8; // keep the panel this far from the viewport edge

/**
 * The small click-outside / Escape popover shared by the backlog cards (status + assignee
 * menus) and the filter facets, so both open the same anchored menu. Pointer-down is
 * stopped on the wrapper so opening a menu on a draggable card never starts a drag.
 *
 * By default the panel is positioned `absolute` under the trigger — fine when no `overflow`
 * ancestor can clip it (header, filter bar). Pass `portal` for triggers that live inside a
 * scroll container (the kanban columns row): the panel then renders in a body portal, fixed
 * to the viewport and flipping above the trigger when there's no room below — matching the
 * DatePicker / ContextMenu so a card menu is never clipped by the board's overflow.
 */
export function Popover({
  trigger,
  children,
  align = 'left',
  side = 'bottom',
  panelClassName,
  portal = false,
  menu = false,
}: {
  trigger: (arg: TriggerArg) => ReactNode;
  children: (close: () => void) => ReactNode;
  align?: 'left' | 'right' | 'center';
  /** Which side of the trigger the portaled panel opens on. `right` flies out beside the trigger
   *  (used by the canvas toolbar so a flyout doesn't cover the tool column); default drops below. */
  side?: 'bottom' | 'right';
  panelClassName?: string;
  portal?: boolean;
  /**
   * Opt-in menu keyboard support. The panel is portaled to `<body>`, so inside a focus-trapping
   * Modal its options would otherwise be unreachable (and Escape would fall through to the modal).
   * With `menu`, focus moves into the panel on open, arrows / Tab rove over its focusables, and
   * focus returns to the trigger on close. Off by default so the board's card/filter popovers keep
   * their exact mouse-first behaviour.
   */
  menu?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on outside click / Escape. The panel is also checked so a click inside a portaled
  // panel (which sits in <body>, outside the wrapper) doesn't count as "outside".
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      // Stop the Escape here so it dismisses only the popover — without this it bubbles on to a
      // surrounding Modal's window-level listener and tears down the whole dialog (see DatePicker,
      // which stops it for the same reason).
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // --- Menu keyboard support (opt-in via `menu`) -------------------------------------------------
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  const focusables = () => (panelRef.current ? Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)) : []);

  // Pull focus into the panel once — after it's placed, so it's visible (a visibility:hidden node
  // can't take focus). The first focusable is a search input for combobox-style menus, else the
  // first option. Guarded so a scroll-driven reposition (pos change) can't yank focus back.
  const menuFocused = useRef(false);
  // The last option index the arrows roved to. If focus ever falls to <body> (a re-render blurs the
  // focused option), the next arrow resumes from HERE instead of snapping back to the first item.
  const rovingIndex = useRef(-1);
  useEffect(() => {
    if (!menu) return;
    if (open && (pos || !portal) && !menuFocused.current) {
      menuFocused.current = true;
      rovingIndex.current = 0;
      focusables()[0]?.focus();
    }
    if (!open) {
      menuFocused.current = false;
      rovingIndex.current = -1;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu, open, pos, portal]);

  // On close via keyboard/selection the focused option unmounts and focus falls to <body>; pull it
  // back to the trigger. If the user moved focus elsewhere (clicked another control), leave it be.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (menu && wasOpen.current && !open && (document.activeElement === document.body || !document.activeElement)) {
      (ref.current?.querySelector('button') as HTMLElement | null)?.focus();
    }
    wasOpen.current = open;
  }, [open, menu]);

  const onPanelKeyDown = (e: ReactKeyboardEvent) => {
    if (!menu) return;
    const items = focusables();
    if (items.length === 0) return;
    const el = document.activeElement as HTMLElement | null;
    // A text field (the SelectPopover search box) keeps its native caret keys; Home/End only rove
    // the option list when focus is on an option, not while typing a query.
    const typing = el?.tagName === 'INPUT' || el?.tagName === 'TEXTAREA';
    const i = el ? items.indexOf(el) : -1;
    const move = (to: number) => {
      e.preventDefault();
      e.stopPropagation();
      items[((to % items.length) + items.length) % items.length]?.focus();
    };
    if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) move(i + 1);
    else if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) move(i - 1);
    else if (e.key === 'Home' && !typing) move(0);
    else if (e.key === 'End' && !typing) move(items.length - 1);
  };

  // Portal placement: fix the panel to the viewport at the trigger, flipping above when there
  // isn't room below, and keep it anchored as the board (or window) scrolls / resizes. The panel
  // renders (hidden) before this runs so its real size drives the flip/clamp with no flash.
  useLayoutEffect(() => {
    if (!portal || !open) return undefined;
    const place = () => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const panel = panelRef.current;
      const pw = panel?.offsetWidth ?? 210;
      const ph = panel?.offsetHeight ?? 0;
      const clamp = (v: number, max: number) => Math.max(MARGIN, Math.min(v, max));
      let rawTop: number;
      let rawLeft: number;
      if (side === 'right') {
        // Beside the trigger, vertically centred; flip to the left if there's no room on the right.
        const spaceRight = window.innerWidth - r.right;
        rawLeft = spaceRight >= pw + GAP + MARGIN || r.left < spaceRight ? r.right + GAP : r.left - GAP - pw;
        rawTop = r.top + r.height / 2 - ph / 2;
      } else {
        const spaceBelow = window.innerHeight - r.bottom;
        const up = ph > 0 && spaceBelow < ph + GAP && r.top > spaceBelow;
        rawTop = up ? r.top - GAP - ph : r.bottom + GAP;
        rawLeft = align === 'right' ? r.right - pw : align === 'center' ? r.left + r.width / 2 - pw / 2 : r.left;
      }
      setPos({ top: clamp(rawTop, window.innerHeight - ph - MARGIN), left: clamp(rawLeft, window.innerWidth - pw - MARGIN) });
    };
    place();
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portal, open, align, side]);

  // Reset the computed position when closed so the next open measures fresh (and stays hidden
  // until it does).
  useEffect(() => {
    if (!open) setPos(null);
  }, [open]);

  const panelBase =
    'thin-scroll z-40 max-h-[320px] min-w-[210px] overflow-auto rounded-xl border border-line bg-surface-raised p-1.5 shadow-2xl shadow-black/60';

  return (
    <div
      ref={ref}
      className="relative"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {trigger({ open, toggle: () => setOpen((o) => !o), close: () => setOpen(false) })}
      {open
        ? portal
          ? createPortal(
              <div
                ref={panelRef}
                data-wb-overlay
                onKeyDown={onPanelKeyDown}
                onPointerDown={(e) => e.stopPropagation()}
                style={{
                  position: 'fixed',
                  top: pos?.top ?? 0,
                  left: pos?.left ?? 0,
                  visibility: pos ? 'visible' : 'hidden',
                }}
                // z-[2100] so a popover opened from inside a Modal (z-2000) sits above it, matching
                // DatePicker; harmless on the board, where nothing else lives between z-40 and 2000.
                className={cn(panelBase, 'z-[2100]', panelClassName)}
              >
                {children(() => setOpen(false))}
              </div>,
              document.body,
            )
          : (
            <div
              ref={panelRef}
              data-wb-overlay
              onKeyDown={onPanelKeyDown}
              className={cn(panelBase, 'absolute top-full mt-1.5', align === 'right' ? 'right-0' : 'left-0', panelClassName)}
            >
              {children(() => setOpen(false))}
            </div>
          )
        : null}
    </div>
  );
}

/** Shared option-row styling for popover menus: leading visual · label · trailing check.
 *  min-h 48 keeps each row a full tap target; the popover scrolls when the list is long. */
export const menuRow =
  'flex min-h-[48px] w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] transition-colors hover:bg-layer focus:bg-layer-strong focus:outline-none focus:ring-2 focus:ring-inset focus:ring-accent/60';

export default Popover;
